// src/Pages/UserReports.js
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useUsersAnalytics from "../../Hooks/Reports/useUsersAnalytics";

const UserReports = ({ period }) => {
  const { usersAnalytics, error, isLoading, reload } = useUsersAnalytics({
    period,
    include_trends: true,
  });

  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return <p className="text-gray-600">جاري تحميل البيانات...</p>;
  }

  if (error) {
    return (
      <div className="text-center">
        <p className="text-red-500 mb-2">{error.message || "حدث خطأ ما"}</p>
        <button
          onClick={reload}
          className="px-4 py-2 bg-main1 text-light rounded-lg"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  if (!usersAnalytics) {
    return <p className="text-gray-500">لا توجد بيانات متاحة</p>;
  }

  const { overview, registration_trend, activity_insights } = usersAnalytics;

  const displayedRecipients = showAll
    ? activity_insights.frequent_recipients
    : activity_insights.frequent_recipients.slice(0, 5);

  return (
    <div className="p-6 font-cairo bg-light min-h-screen">
      {/* 🔹 الملخص */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <SummaryCard title="إجمالي المستخدمين" value={overview.total_users} />
        <SummaryCard title="المستخدمون النشطون" value={overview.active_users} />
        <SummaryCard title="تسجيلات جديدة" value={overview.new_registrations} />
        <SummaryCard title="عدد التجار" value={overview.merchants} />
        <SummaryCard title="عدد المدراء" value={overview.super_admins} />
      </div>

      {/* 🔹 ترند التسجيلات + نشاط المستخدمين */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* الرسم البياني */}
        <div className="bg-grayish shadow rounded-2xl p-6 animate-fadeUpSmooth">
          <h2 className="text-xl font-semibold text-main1 mb-4">
            التسجيلات الجديدة
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={registration_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="registrations"
                fill="#1C4130"
                radius={[6, 6, 0, 0]}
                barSize={40}
                name="التسجيلات"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* جدول النشاط */}
        <div className="bg-grayish shadow rounded-2xl p-6 animate-fadeUpSmooth flex flex-col">
          <div className="flex items-center justify-between">
            <div>
              <h6 className="text-xl font-semibold text-main1 mb-2">
                إحصائيات النشاط
              </h6>
              <p className="text-gray-700 text-sm mb-2">
                معدل النشاط:{" "}
                <span className="text-sm text-main1 mt-1">
                  {activity_insights.activity_rate.toFixed(2)}%
                </span>
              </p>
            </div>
            {activity_insights.frequent_recipients.length > 5 && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="py-2 px-4 bg-main1 text-light rounded-2xl transition self-center"
              >
                {showAll ? "إخفاء" : "عرض المزيد"}
              </button>
            )}
          </div>

          <div className="overflow-x-auto rounded-2xl flex-grow">
            <table className="min-w-full text-sm text-main1 font-cairo">
              <thead>
                <tr className="bg-grayish text-main1">
                  <th className="p-4 text-right">رقم الهاتف</th>
                  <th className="p-4 text-center">عدد العمليات</th>
                  <th className="p-4 text-center">إجمالي المبالغ</th>
                </tr>
              </thead>
              <tbody>
                {displayedRecipients.map((rec, idx) => (
                  <tr
                    key={idx}
                    className="border-b last:border-b-0 hover:bg-grayish/60 transition"
                  >
                    <td className="p-4 text-right">{rec.phone}</td>
                    <td className="p-4 text-center">{rec.transaction_count}</td>
                    <td className="p-4 text-center font-bold">
                      {rec.total_amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🔹 Component لبطاقات الملخص
const SummaryCard = ({ title, value }) => (
  <div className="bg-grayish shadow rounded-2xl p-4 flex flex-col items-center justify-center animate-fadeUpSmooth">
    <p className="text-sm text-gray-500">{title}</p>
    <h3 className="text-xl font-bold text-main1 mt-2">{value}</h3>
  </div>
);

export default UserReports;
